import React, { FC, useState, useEffect } from 'react';
import { debounce } from 'debounce';
// common components
import { StyledMap, MapControl } from '@ems/client-design-system';
// functions
import {
  useMapRef,
  useMapApis,
  useMapProps,
  useMapConfig,
  useMapCenter,
  useSingleDateTile,
} from 'src/@app/functions/map';
import {
  useMapLayer,
  useInfringementMap,
  useMapTracksFilter,
} from 'src/@app/functions/infringementsOnMap';
import { useLanguageSelectors } from 'src/@app/reducers/languageReducer';
import { useAuthSelectors } from 'src/@auth/reducers/authReducer';
// @ts-ignore
import sassVars from 'src/styles/vars.scss';
import {
  flyTo,
  useMapHoverOperations,
  useMapHover,
  useMapClick,
  useMapSelection,
} from 'src/utils/mapHelpers';
import { OperationPopup } from 'src/components';
import { useInAirPlayback } from 'src/utils/playback';

export const MapContainer: FC<{
  time: string;
  operationId: number;
  operation: any;
  position: any;
  currentTime: number;
  isPlaybackMode: boolean;
  inAirData: any;
}> = ({ time, operationId, operation, position, currentTime, isPlaybackMode, inAirData }) => {
  // map ref
  const [mapNode, mapRef] = useMapRef();
  // get map apis
  const mapApis: any = useMapApis(mapNode);
  // get map props from config
  const { viewportFromProps, ...mapProps } = useMapProps('2D');

  const [viewport, setViewport] = useState(viewportFromProps);

  useMapCenter(viewport, setViewport, position, operationId);

  // get mapbox config values required to add source and styles
  const mapBoxConfig = useMapConfig();
  // getting map style layers
  const layers = useMapLayer({ mapApis, mapBoxConfig });

  const { dateString, datesArray } = useSingleDateTile(
    mapApis,
    mapBoxConfig,
    layers,
    time,
    operationId
  );

  const { tracks } = useInfringementMap(
    'op-summary',
    mapApis,
    mapBoxConfig,
    dateString,
    operationId,
    operation,
    null, // infTypeId
    0,
    null,
    true,
    time
  );

  // extrack map filters for the features
  const { infringementFilter } = useMapTracksFilter(tracks);

  // handle hover and get hovered operation
  const { hoveredOperation, handleHover, setHoveredOperation } = useMapHoverOperations(
    viewport,
    mapApis,
    datesArray,
    infringementFilter
  );
  // set map selection for the hover object
  useMapHover(hoveredOperation, mapApis);

  const resetView = () => {
    if (mapApis) {
      const resetViewport = Object.assign({}, viewportFromProps, { zoom: viewport.zoom });
      flyTo(mapApis, resetViewport).then(() => {
        setViewport(Object.assign({}, viewport, resetViewport));
      });
    }
  };

  // Map selection
  const [showSelected, setShowSelected] = useState(false);
  const [selectedOperations, setSelectedOperations]: any = useState(null);

  // click handlers that provide selected operations
  const { handleClick, selectedOperation } = useMapClick(
    hoveredOperation,
    setShowSelected,
    mapApis,
    datesArray,
    infringementFilter,
    selectedOperations,
    setSelectedOperations
  );

  // set feature state for the hovered operation
  useMapSelection(selectedOperations, mapApis, 'infringement-select');

  // reset hover and select when selection changes
  useEffect(() => {
    setShowSelected(false);
    setHoveredOperation(null);
    setSelectedOperations([]);
  }, [dateString]);

  // get field labels from language selectors
  const languageSelectors = useLanguageSelectors();
  const {
    fields: { operations: opsFields },
    abbreviations: { operations: opsAbbreviation },
  } = languageSelectors.getLanguage();
  const labels = Object.assign(opsFields, opsAbbreviation);

  const accessToken = useAuthSelectors().getAccessToken();
  useInAirPlayback(mapApis, inAirData, currentTime, isPlaybackMode, operationId);

  return (
    <StyledMap
      {...mapProps}
      viewport={viewport}
      onViewportChange={viewport => {
        viewport.maxPitch = 0;
        setViewport(viewport);
      }}
      ref={mapRef}
      onClick={handleClick}
      onHover={debounce(handleHover, 5)}
      transformRequest={
        mapBoxConfig && mapBoxConfig.transformRequest && mapBoxConfig.transformRequest(accessToken)
      }>
      <MapControl resetView={resetView} />
      {showSelected &&
        selectedOperation &&
        selectedOperation.latitude &&
        selectedOperation.longitude && (
          <OperationPopup
            selectedOperation={selectedOperation}
            onClose={() => setShowSelected(false)}
            labels={labels}
          />
        )}
      {!showSelected &&
        hoveredOperation &&
        hoveredOperation.latitude &&
        hoveredOperation.longitude && (
          <OperationPopup
            selectedOperation={hoveredOperation}
            onClose={() => {
              console.log(close);
            }}
            labels={labels}
          />
        )}
    </StyledMap>
  );
};
